<template>
  <div
    v-if="!inline"
    class="maplibregl-ctrl maplibregl-ctrl-group DragDropGeodata"
    @dragover.prevent="isDragging = true"
    @dragleave.prevent="isDragging = false"
    @drop.prevent="handleDrop"
  >
    <input
      ref="fileInputRef"
      type="file"
      accept=".geojson,.json,.kml,.kmz,application/geo+json,application/json,application/vnd.google-earth.kml+xml,application/vnd.google-earth.kmz"
      class="DragDropGeodata__input"
      :aria-label="dragDropAriaLabel"
      @change="handleFileSelect"
    />
    <MapControlTooltip :text="$t('geoportal.mapControls.importGeodata')">
      <button
        type="button"
        class="DragDropGeodata__btn"
        :class="{ 'DragDropGeodata__btn--active': isDragging }"
        :aria-label="dragDropAriaLabel"
        @click="triggerFileInput"
      >
        <Icon icon="mdi:upload" :size="20" color="var(--color-main)" />
      </button>
    </MapControlTooltip>
  </div>
  <div
    v-else
    class="DragDropGeodata DragDropGeodata--inline"
    @dragover.prevent="isDragging = true"
    @dragleave.prevent="isDragging = false"
    @drop.prevent="handleDrop"
  >
    <input
      ref="fileInputRef"
      type="file"
      accept=".geojson,.json,.kml,.kmz,application/geo+json,application/json,application/vnd.google-earth.kml+xml,application/vnd.google-earth.kmz"
      class="DragDropGeodata__input"
      :aria-label="dragDropAriaLabel"
      @change="handleFileSelect"
    />
    <MapControlTooltip :text="$t('geoportal.mapControls.importGeodata')">
      <button
        type="button"
        class="DragDropGeodata__btn"
        :class="{ 'DragDropGeodata__btn--active': isDragging }"
        :aria-label="dragDropAriaLabel"
        @click="triggerFileInput"
      >
        <Icon icon="mdi:upload" :size="20" color="var(--color-main)" />
      </button>
    </MapControlTooltip>
  </div>
</template>

<script setup lang="ts">
defineProps<{
  inline?: boolean
}>()

import Icon from '@/components/ui/Icon.vue'
import MapControlTooltip from '@/components/map/controls/MapControlTooltip.vue'
import { NotificationType } from '@/models/enums/app/NotificationType'
import { addNotification } from '@/services/notifications/NotificationService'
import MapService from '@/services/map/MapService'
import { useMyMapStore } from '@/stores/myMapStore'
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'

const myMapStore = useMyMapStore()
const { t } = useI18n()
const fileInputRef = ref<HTMLInputElement | null>(null)
const isDragging = ref(false)

const dragDropAriaLabel = computed(() =>
  t('geodata.dragDrop.ariaLabel', 'Importer des données géographiques (GeoJSON)'),
)

function getGeodataErrorMessage(err: unknown): string {
  const msg = err instanceof Error ? err.message : ''
  if (
    msg.includes('Only GeoJSON, KML, or KMZ') ||
    msg.includes('only .geojson') ||
    msg.includes('only .kml')
  ) {
    return t('geodata.dragDrop.badExtension')
  }
  if (msg.includes('Invalid GeoJSON') || msg.includes('unknown or missing type')) {
    return t('geodata.dragDrop.onlyGeoJsonFormat')
  }
  if (
    msg.includes('Invalid KML') ||
    msg.includes('KMZ archive contains no') ||
    msg.includes('conversion produced no')
  ) {
    return t('geodata.dragDrop.invalidKmlKmz')
  }
  return t('geodata.dragDrop.parseError')
}

function triggerFileInput() {
  fileInputRef.value?.click()
}

async function processFile(file: File) {
  if (!myMapStore.myMap?.map) {
    addNotification(t('geodata.dragDrop.mapNotReady'), NotificationType.INFO)
    return
  }
  try {
    const { name, geojson } = await MapService.parseGeoJsonFromFile(file)
    const added = myMapStore.addUserDroppedLayer(name, geojson)
    if (added) {
      myMapStore.zoomToUserDroppedLayer(added.id)
      addNotification(
        t('geodata.dragDrop.success', 'Données géographiques ajoutées à la carte.'),
        NotificationType.SUCCESS,
      )
    } else {
      addNotification(
        t('geodata.dragDrop.mapNotReady', "La carte n'est pas prête. Réessayez dans un instant."),
        NotificationType.INFO,
      )
    }
  } catch (err) {
    const message = getGeodataErrorMessage(err)
    addNotification(message, NotificationType.ERROR)
  }
  if (fileInputRef.value) {
    fileInputRef.value.value = ''
  }
  isDragging.value = false
}

function handleFileSelect(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (file) processFile(file)
}

function handleDrop(event: DragEvent) {
  isDragging.value = false
  const file = event.dataTransfer?.files?.[0]
  if (file) processFile(file)
}
</script>

<style lang="scss" scoped>
.DragDropGeodata {
  flex-flow: row nowrap !important;
  margin-right: 0.3rem !important;

  &--inline {
    display: flex;
    margin-right: 0 !important;
  }
}

.DragDropGeodata__input {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  pointer-events: none;
}

.DragDropGeodata__btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition:
    opacity 0.2s ease,
    background 0.25s ease,
    transform 0.2s ease;

  &:hover {
    opacity: 0.85;
  }

  &--active {
    background: rgba(var(--v-theme-main-blue), 0.12);
    transform: scale(1.05);
  }
}
</style>
